import React from "react";
import Helmet from "react-helmet";
import { Alternate } from "../../model/language";
import { PageMetadata } from "../../model/metadata";
import { useDeploymentDetails } from "../../model/deployment";
import { useGatsbyLocation } from "../../utils/useGatsbyLocation";
import { siteUrlLanguageToHrefLang } from "../../../languages";
import favicon from "./favicon.ico";

interface HeadProps {
  metadata: PageMetadata;
}

const CrowdinLiveTranslate = () => {
  const { url } = useDeploymentDetails();
  const crowdinScript = `
    (function() {
      function escape() {
        window.location.href = ${JSON.stringify(url)};
      }
      window._jipt = [ 
        ["project", "microbitorg"],
        ['escape', escape]
      ];
    })();
  `;
  return (
    <Helmet>
      <script type="text/javascript">{crowdinScript}</script>
      <script type="text/javascript" src="//cdn.crowdin.com/jipt/jipt.js" />
      {/* Avoid indexing these translation helper pages */}
      <meta name="robots" content="noindex" />
    </Helmet>
  );
};

const SocialComponent = ({ value }: { value: PageMetadata }) => {
  const {
    page: { socialImage: image }
  } = value;
  const twitterCardType = image ? "summary_large_image" : "summary";
  const ogTitle = value.page.socialTitle || value.title;
  const ogDescription =
    value.page.socialDescription || value.page.metaDescription;
  const allowIndexing = process.env.GATSBY_STAGE === "PRODUCTION";
  return (
    <Helmet>
      {}
      {!allowIndexing && <meta name="robots" content="noindex" />}
      <meta name="twitter:card" content={twitterCardType} />
      {ogTitle && <meta property="og:title" content={ogTitle} />}
      {ogDescription && (
        <meta property="og:description" content={ogDescription} />
      )}
      {image && (
        <meta property="og:image" content={`${image.asset.url}?q=95`} />
      )}
      {image && (
        <meta
          property="og:image:width"
          content={image.asset.metadata.dimensions.width.toString()}
        />
      )}
      {image && (
        <meta
          property="og:image:height"
          content={image.asset.metadata.dimensions.height.toString()}
        />
      )}
      {value.type && value.type !== "website" && (
        <meta property="og:type" content={value.type} />
      )}
      {value.type === "article" && value.page.date && (
        <meta property="article:published_time" content={value.page.date} />
      )}
    </Helmet>
  );
};

const Alternates = ({ value }: { value: Alternate[] }) => (
  <Helmet>
    {value
      .filter(a => !a.fallback)
      .map(({ language, url }) => (
        <link
          key={language}
          rel="alternate"
          hrefLang={siteUrlLanguageToHrefLang(language)}
          href={url}
        />
      ))}
  </Helmet>
);

const Head = (props: HeadProps) => {
  const { pathname: url } = useGatsbyLocation();
  const { metadata } = props;
  const deployment = useDeploymentDetails();
  const canonicalUrl = `${deployment.url}${metadata.canonicalUrlOverride ||
    url}`;
  const { metaDescription } = metadata.page;
  return (
    <>
      <Helmet>
        <meta http-equiv="X-UA-Compatible" content="IE=edge" />
        <title>{metadata.title} | micro:bit</title>
        {metaDescription && (
          <meta name="description" content={metaDescription} />
        )}
        <meta property="og:url" content={canonicalUrl} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="shortcut icon" type="image/ico" href={favicon} />
      </Helmet>
      {metadata.page.language === "page-translate" && <CrowdinLiveTranslate />}
      <SocialComponent value={metadata} />
      <Alternates value={metadata.alternates} />
    </>
  );
};

export default Head;
